<template>
	<AppLayout :title="trans('settings.companies.title')">
		<Modal id="create-company-modal">
			<template #title>{{ trans('settings.companies.new.title') }}</template>
			<LabeledObject required>
				<template #label>{{ trans('settings.companies.name') }}</template>
				<input v-model="newCompany.name" type="text">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-company-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Pagination :resource="companies"/>
		<Empty v-if="companies.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="company in companies.data" :key="`companies-${company.id}`">
				<LabeledObject required>
					<template #label>{{ trans('settings.companies.name') }}</template>
					<input v-model="company.name" type="text">
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.activities.fieldTrip.title') }}</template>
					<p>{{ company.field_trips_count }}</p>
				</LabeledObject>
				<template #footer>
					<SecondaryButton :color="Color.Green" @click.stop="save(company)">
						<i class="fa-solid fa-save mr-2"/>
						{{ trans('general.save') }}
					</SecondaryButton>
					<SoftButton v-if="company.field_trips_count === 0" :color="Color.Red" @click.stop="destroy(company)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
		<Pagination :resource="companies"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Pagination from '@/Components/UI/Pagination.vue'
import {type Company} from '@/Types/Settings/Company'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	companies:PaginatedModel<Company>,
}>()

const working:Ref<boolean> = ref(false)
const companies:Ref<PaginatedModel<Company>> = ref(props.companies)
const newCompany:Ref<Company> = ref({
	name: '',
} as Company)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('settings.companies.store'), newCompany.value).then(({data}:{data:Company}) => {
			companies.value.data.push(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function save(company:Company):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('settings.companies.update', company.id), company).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy(company:Company):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('settings.companies.delete', company.id)).then(() => {
			const index = companies.value.data.findIndex((c:Company) => c.id === company.id)
			if(index > -1){
				companies.value.data.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>